import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import { useImmer } from 'use-immer'

import { parseParametrizatioDepedentsCDData } from '@helper/grid/parametrization-curve'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'
import parametrizationCurves from '@service/parametrization-curves'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import { ICurveDepedentsCDProvider } from '@base/@types/providers/Parametrization/CurveDepedentsCDProvider'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate
} from '@base/@types/providers/Parametrization/CurveDepedentsCDProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationCurveDepedentsCDHeader from '@base/variables/headers/parametrization/curve-depedents-cd'

const ParametrizationCurveDepedentsCDProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(false)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState(
    'PRM Centro Depedentes CD - Relatório'
  )
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: [],
    products: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = ({ name, value }: ITooglesParams) => {
    updateToogleParams(draft => {
      draft[name] = value
    })
  }

  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = ({ name, value }: IUpdateParamsDate) => {
    updateDatesParams(draft => {
      draft[name] = value
    })
  }

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    id: 0,
    idCentroDistribuicao: 0,
    idLoja: 0
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )

  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getStore()
      const { data: products } = await parametrization.getDistributionCenters()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data),
          products: parseCommonFilters(products.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response

      setError(
        data.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    produtoSemFornecedor: false
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationCurveDepedentsCDHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationCurveDepedentsCDHeader')) {
      encrypteHeaderStorange('parametrizationCurveDepedentsCDHeader', headers)
    } else {
      localStorage.removeItem('parametrizationCurveDepedentsCDHeader')
      encrypteHeaderStorange('parametrizationCurveDepedentsCDHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {}, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationCurveDepedentsCDHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationCurveDepedentsCDHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams(draft => {
      if (name === 'curvaIgualZero') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
      if (name === 'produtoSemFornecedor') {
        draft[name] = Boolean(value)
      } else {
        draft[name] = value
      }
    })
  }

  const handlePageChange = useCallback(async (_page: number) => {
    setLoading(true)
  }, [])

  const getData = useCallback(async () => {
    setLoading(true)
    // await getMaintence()

    encrypteHeaderStorange('parametrizationCurveDepedentsCDHeader', headers)

    handleStorange()

    try {
      parametrizationCurves
        .getParamsCurveDepedetensCD()
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })

          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    } catch {
      setLoading(false)
    }
  }, [headers, handleStorange, updateData, updatePagination])

  const updateDataCurve = useCallback(async () => {
    setLoading(true)

    try {
      const { data: _res } =
        await parametrizationCurves.postParamsCurveDepedetensCD({
          id: paramsEdit.id,
          idLoja: paramsEdit.idLoja.value,
          idCentroDistribuicao: paramsEdit.idCentroDistribuicao.value
        })
      toast({
        title: 'Sucesso.',
        description: 'Centro Depedente CD editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [paramsEdit, toast, getData, setLoading, setSelectedOne])

  const deleteData = useCallback(async () => {
    setLoading(true)

    try {
      await parametrizationCurves.removeParamsCurveDepedetensCD({
        ids: [Number(selected)]
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [selected, getData, toast, setLoading, setSelectedOne])

  const createData = useCallback(async () => {
    setLoading(true)

    const validation = data.findIndex(item => {
      return (
        item.idCentroDistribuicao === paramsEdit.idCentroDistribuicao.value &&
        item.idLoja === paramsEdit.idLoja.value
      )
    })

    if (validation !== -1) {
      toast({
        title: 'Atencao.',
        description: 'Esses centro já esta cadatrado',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      setLoading(false)

      return
    }
    try {
      await parametrizationCurves.postParamsCurveDepedetensCD({
        id: paramsEdit.id,
        idLoja: paramsEdit.idLoja.value,
        idCentroDistribuicao: paramsEdit.idCentroDistribuicao.value
      })
      toast({
        title: 'Sucesso.',
        description: 'Curva Execucao cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData()
  }, [
    data,
    getData,
    paramsEdit.idCentroDistribuicao.value,
    paramsEdit.idLoja.value,
    paramsEdit.id,
    toast
  ])

  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return []
  }, [])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'parameterCurveDepedentsCD',
            data,
            headers,
            parser: parseParametrizatioDepedentsCDData
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [toggleColumn, data, headers])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'id',

          value: idData[0].id
        })
        handleUpdateEditParams({
          name: 'idCentroDistribuicao',
          value: {
            label: idData[0].idCentroDistribuicao
              ? idData[0].idCentroDistribuicao +
                ' - ' +
                idData[0].centroDistribuicaoDescricao
              : '',
            value: idData[0].idCentroDistribuicao
          }
        })
        handleUpdateEditParams({
          name: 'idLoja',
          value: {
            label: idData[0].idLoja
              ? idData[0].idLoja + ' - ' + idData[0].lojaDescricao
              : '',
            value: idData[0].idLoja
          }
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selected

      setSelectedOne(prev => (prev !== similarProduct ? similarProduct : 0))
      if (newSelected === similarProduct) {
        updateEditParams({
          id: 0,
          idCentroDistribuicao: 0,
          idLoja: 0
        })
      } else {
        handleEditParams(index['id-id'])
      }
    },
    [setSelectedOne, handleEditParams, selected, updateEditParams]
  )

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selected,
        isMulti: false
      }
    ]
  }, [selected, handleSelect])

  return (
    <ParametrizationCurveDepedentsCDContext.Provider
      value={{
        loading,
        loadingEdit,
        data: parseParametrizatioDepedentsCDData(data),
        params,
        headers,
        fetching,
        actions,
        tableInteraction,
        selected,
        filterEdit,
        paramsEdit,
        paramsDates,
        tooglesTable,
        filename,
        query,
        filters,
        hiddenColumns,
        handlePageChange,
        pagination,
        fastQuery,
        filtersList,
        toggleColumn,
        handleUpdateParams,
        handleSelect,
        getCenters,
        handleParamsDate,
        setQuery,
        getData,
        exportData,
        createData,
        handleUpdateEditParams,
        handleToogleTable,
        updateDataCurve,
        deleteData,
        setFilters,
        setFilename
      }}
    >
      {children}
    </ParametrizationCurveDepedentsCDContext.Provider>
  )
}

export default ParametrizationCurveDepedentsCDProvider
export const ParametrizationCurveDepedentsCDContext =
  createContext<ICurveDepedentsCDProvider>({} as ICurveDepedentsCDProvider)
